import React, { useState, useRef, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { 
  Eye, 
  Download, 
  Save, 
  RotateCcw, 
  Maximize2, 
  QrCode,
  Sparkles,
  Box,
  Ruler,
  Printer
} from "lucide-react";
import { BUSINESS_CARD_STANDARDS, GOOGLE_FONTS } from "../../constants/businessCardStandards";
import html2canvas from 'html2canvas';

const EnhancedCard3DPreview = ({ 
  cardData, 
  template, 
  onSave, 
  onGeneratePDF, 
  onContinue, 
  loading 
}) => {
  const [previewMode, setPreviewMode] = useState("css3d");
  const [isFlipped, setIsFlipped] = useState(false);
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);

  const orientation = cardData.orientation || "horizontal";
  const fontSettings = cardData.fontSettings || {};
  const customPhoto = cardData.customPhoto;

  const getCurrentDimensions = () => {
    const dims = BUSINESS_CARD_STANDARDS.dimensions;
    return orientation === "horizontal" 
      ? { width: dims.width, height: dims.height, widthPx: dims.widthPx, heightPx: dims.heightPx }
      : { width: dims.height, height: dims.width, widthPx: dims.heightPx, heightPx: dims.widthPx };
  };

  const getFontStyle = (elementKey) => {
    const settings = fontSettings[elementKey] || {};
    const googleFont = GOOGLE_FONTS.find(f => f.name === settings.font);
    
    return {
      fontFamily: googleFont?.family || 'Inter, sans-serif',
      fontSize: `${settings.size || 12}pt`,
      fontWeight: settings.weight || '400',
      color: settings.color || '#000000',
      lineHeight: 1.2
    };
  };

  // Mock QR code generation
  const generateQRData = () => {
    return `BEGIN:VCARD
VERSION:3.0
FN:${cardData.personalName}
ORG:${cardData.companyName}
TEL:${cardData.phone}
EMAIL:${cardData.email}
URL:${cardData.website}
ADR:;;${cardData.address};;;;
END:VCARD`;
  };

  const downloadHighResImage = async () => {
    if (!cardRef.current) return;

    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 3, // High DPI for printing
        backgroundColor: 'white',
        useCORS: true,
        allowTaint: true
      });

      const link = document.createElement('a');
      link.download = `business-card-${cardData.companyName?.replace(/\s+/g, '-').toLowerCase()}-high-res.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error generating high-res image:', error);
    }
  };

  const CSS3DPreview = () => {
    const dims = getCurrentDimensions();
    
    return (
      <div className="perspective-1000 w-full h-80 flex items-center justify-center">
        <div 
          ref={cardRef}
          className={`relative transition-transform duration-700 transform-style-3d ${
            isFlipped ? 'rotate-y-180' : ''
          }`}
          style={{
            width: `${dims.widthPx / 3}px`,
            height: `${dims.heightPx / 3}px`,
            transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y + (isFlipped ? 180 : 0)}deg)`
          }}
        >
          {/* Front of card */}
          <div 
            className="absolute inset-0 w-full h-full rounded-lg shadow-xl backface-hidden overflow-hidden"
            style={{ 
              backgroundColor: template?.colors[0] || '#ffffff',
              backgroundImage: template?.preview ? `url(${template.preview})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-transparent"></div>
            <div className="relative z-10 p-6 h-full flex flex-col justify-between text-white">
              <div>
                {/* Logo or Photo */}
                <div className="mb-3">
                  {customPhoto ? (
                    <img 
                      src={customPhoto} 
                      alt="Profile" 
                      className="w-16 h-16 object-cover rounded-full border-2 border-white/20"
                    />
                  ) : cardData.logoUrl && (
                    <img 
                      src={cardData.logoUrl} 
                      alt="Logo" 
                      className="w-12 h-12 object-cover rounded"
                    />
                  )}
                </div>

                {/* Company Name */}
                <h2 
                  className="mb-2" 
                  style={getFontStyle('companyName')}
                >
                  {cardData.companyName || 'Company Name'}
                </h2>

                {/* Slogan */}
                {cardData.slogan && (
                  <p className="text-sm opacity-90 italic mb-2">{cardData.slogan}</p>
                )}
              </div>

              <div className="space-y-1" style={getFontStyle('contactInfo')}>
                <p style={getFontStyle('personalName')}>
                  {cardData.personalName || 'Personal Name'}
                </p>
                {cardData.jobTitle && (
                  <p style={getFontStyle('jobTitle')}>{cardData.jobTitle}</p>
                )}
                {cardData.email && <p>{cardData.email}</p>}
                {cardData.phone && <p>{cardData.phone}</p>}
                {cardData.website && <p>{cardData.website}</p>}
              </div>
            </div>
          </div>

          {/* Back of card */}
          <div 
            className="absolute inset-0 w-full h-full rounded-lg shadow-xl backface-hidden rotate-y-180 overflow-hidden"
            style={{ backgroundColor: template?.colors[1] || '#f8f9fa' }}
          >
            <div className="p-6 h-full flex items-center justify-between">
              <div className="flex-1 text-gray-800">
                <h3 className="text-lg font-bold mb-3" style={getFontStyle('companyName')}>
                  Contact Information
                </h3>
                <div className="space-y-1" style={getFontStyle('contactInfo')}>
                  {cardData.address && (
                    <p className="leading-relaxed">{cardData.address}</p>
                  )}
                  {cardData.email && <p>Email: {cardData.email}</p>}
                  {cardData.phone && <p>Phone: {cardData.phone}</p>}
                  {cardData.website && <p>Web: {cardData.website}</p>}
                </div>
              </div>
              
              {/* QR Code Area */}
              <div className="w-20 h-20 bg-white border-2 border-gray-300 rounded flex items-center justify-center ml-4">
                <div className="text-center">
                  <QrCode className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-500">QR Code</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ThreeJSPreview = () => (
    <div className="w-full h-80 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
      <div className="text-center space-y-4">
        <Box className="h-16 w-16 text-gray-400 mx-auto" />
        <div>
          <h3 className="text-lg font-semibold text-gray-700">Three.js 3D Preview</h3>
          <p className="text-sm text-gray-500">Realistic 3D rendering with shadows and lighting</p>
          <Badge variant="secondary" className="mt-2">Coming Soon</Badge>
        </div>
      </div>
    </div>
  );

  const Interactive360Preview = () => (
    <div className="w-full h-80 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg flex items-center justify-center">
      <div className="text-center space-y-4">
        <RotateCcw className="h-16 w-16 text-purple-400 mx-auto animate-spin" />
        <div>
          <h3 className="text-lg font-semibold text-purple-700">360° Interactive Preview</h3>
          <p className="text-sm text-purple-500">Drag to rotate and interact with your business card</p>
          <Badge variant="secondary" className="mt-2">Advanced Feature</Badge>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Eye className="h-5 w-5 text-emerald-600" />
            <span>Professional Business Card Preview</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Card Specifications */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Ruler className="h-5 w-5 text-blue-600" />
                <div>
                  <h4 className="font-semibold text-blue-900">USA Standard Business Card</h4>
                  <p className="text-sm text-blue-700">
                    {getCurrentDimensions().width}" × {getCurrentDimensions().height}" 
                    ({orientation}) • 300 DPI • Print Ready
                  </p>
                </div>
              </div>
              <Badge variant="outline" className="bg-white text-blue-800 border-blue-200">
                Professional Grade
              </Badge>
            </div>
          </div>

          <Tabs value={previewMode} onValueChange={setPreviewMode} className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="css3d" className="text-xs">3D Preview</TabsTrigger>
              <TabsTrigger value="threejs" className="text-xs">Realistic 3D</TabsTrigger>
              <TabsTrigger value="interactive" className="text-xs">Interactive 360°</TabsTrigger>
            </TabsList>

            <TabsContent value="css3d" className="space-y-4">
              <div className="bg-gradient-to-br from-slate-100 to-gray-200 rounded-lg p-8">
                <CSS3DPreview />
              </div>
              
              <div className="flex items-center justify-center space-x-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsFlipped(!isFlipped)}
                  className="flex items-center space-x-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  <span>Flip Card</span>
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRotation({ x: rotation.x + 15, y: rotation.y + 15 })}
                  className="flex items-center space-x-2"
                >
                  <Box className="h-4 w-4" />
                  <span>Rotate</span>
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadHighResImage}
                  className="flex items-center space-x-2"
                >
                  <Download className="h-4 w-4" />
                  <span>High-Res PNG</span>
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="threejs">
              <div className="rounded-lg p-8">
                <ThreeJSPreview />
              </div>
            </TabsContent>

            <TabsContent value="interactive">
              <div className="rounded-lg p-8">
                <Interactive360Preview />
              </div>
            </TabsContent>
          </Tabs>

          {/* Typography Summary */}
          <div className="mt-6 p-4 bg-green-50 rounded-lg">
            <h4 className="font-semibold text-green-900 mb-2">Typography Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-sm text-green-800">
              <div>
                <p><strong>Company Name:</strong> {fontSettings.companyName?.font || 'Inter'} {fontSettings.companyName?.size || 16}pt</p>
                <p><strong>Personal Name:</strong> {fontSettings.personalName?.font || 'Inter'} {fontSettings.personalName?.size || 12}pt</p>
              </div>
              <div>
                <p><strong>Job Title:</strong> {fontSettings.jobTitle?.font || 'Inter'} {fontSettings.jobTitle?.size || 10}pt</p>
                <p><strong>Contact Info:</strong> {fontSettings.contactInfo?.font || 'Inter'} {fontSettings.contactInfo?.size || 8}pt</p>
              </div>
            </div>
          </div>

          {/* QR Code Info */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-start space-x-3">
              <QrCode className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-900">QR Code Integration</h4>
                <p className="text-sm text-blue-700 mt-1">
                  A QR code will be automatically generated on the back of your card with all contact information. 
                  When scanned, it will allow people to instantly add your details to their contacts.
                </p>
                <div className="mt-2 text-xs text-blue-600 bg-blue-100 p-2 rounded font-mono">
                  {generateQRData()}
                </div>
              </div>
            </div>
          </div>

          {/* Print Quality Notice */}
          <div className="mt-6 p-4 bg-orange-50 rounded-lg">
            <div className="flex items-start space-x-3">
              <Printer className="h-5 w-5 text-orange-600 mt-0.5" />
              <div className="text-sm text-orange-800">
                <p className="font-semibold mb-1">Print Quality Guarantee</p>
                <p>Your business card is designed at 300 DPI with proper bleed zones for professional printing. Colors and text will be crisp and clear when printed at standard size.</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
            <Button
              onClick={onSave}
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700"
            >
              {loading ? (
                "Saving..."
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Business Card
                </>
              )}
            </Button>
            
            <Button
              onClick={onGeneratePDF}
              disabled={loading}
              variant="outline"
              className="flex-1"
            >
              {loading ? (
                "Generating..."
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Generate Print PDF
                </>
              )}
            </Button>
            
            <Button
              onClick={onContinue}
              className="flex-1 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Continue to Payment
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EnhancedCard3DPreview;